import React from 'react';

/*
 * One icon per top-level category in backend/categorySeeder.js's MASTER_TAXONOMY.
 *
 * The taxonomy authors a lucide icon name per category ("ShirtIcon",
 * "CpuIcon", ...) but lucide is not a dependency here, so each name is mapped to
 * its nearest @mui/icons-material Outlined equivalent — already a dependency,
 * and visually consistent across all 31 entries.
 *
 * This replaced eight hand-drawn SVGs whose slug cases ('fashion', 'home',
 * 'accessories', 'more') predated the taxonomy and matched only 2 of the 31
 * real slugs, so 29 categories fell through to a generic grid glyph.
 *
 * Icons inherit `currentColor`, so colour is a CSS concern rather than 31
 * hardcoded hex values.
 */

import CheckroomOutlined from '@mui/icons-material/CheckroomOutlined';
import IceSkatingOutlined from '@mui/icons-material/IceSkatingOutlined';
import SmartphoneOutlined from '@mui/icons-material/SmartphoneOutlined';
import DevicesOtherOutlined from '@mui/icons-material/DevicesOtherOutlined';
import LaptopMacOutlined from '@mui/icons-material/LaptopMacOutlined';
import TvOutlined from '@mui/icons-material/TvOutlined';
import KitchenOutlined from '@mui/icons-material/KitchenOutlined';
import ChairOutlined from '@mui/icons-material/ChairOutlined';
import LocalGroceryStoreOutlined from '@mui/icons-material/LocalGroceryStoreOutlined';
import SpaOutlined from '@mui/icons-material/SpaOutlined';
import MonitorHeartOutlined from '@mui/icons-material/MonitorHeartOutlined';
import WatchOutlined from '@mui/icons-material/WatchOutlined';
import DiamondOutlined from '@mui/icons-material/DiamondOutlined';
import LuggageOutlined from '@mui/icons-material/LuggageOutlined';
import FitnessCenterOutlined from '@mui/icons-material/FitnessCenterOutlined';
import ToysOutlined from '@mui/icons-material/ToysOutlined';
import ChildFriendlyOutlined from '@mui/icons-material/ChildFriendlyOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import DirectionsCarOutlined from '@mui/icons-material/DirectionsCarOutlined';
import HandymanOutlined from '@mui/icons-material/HandymanOutlined';
import FormatPaintOutlined from '@mui/icons-material/FormatPaintOutlined';
import PetsOutlined from '@mui/icons-material/PetsOutlined';
import BusinessCenterOutlined from '@mui/icons-material/BusinessCenterOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import MicrowaveOutlined from '@mui/icons-material/MicrowaveOutlined';
import CardGiftcardOutlined from '@mui/icons-material/CardGiftcardOutlined';
import ExploreOutlined from '@mui/icons-material/ExploreOutlined';
import PianoOutlined from '@mui/icons-material/PianoOutlined';
import FactoryOutlined from '@mui/icons-material/FactoryOutlined';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';

/* Keyed on the taxonomy's `icon` field. */
const BY_ICON_NAME = {
    ShirtIcon: CheckroomOutlined,
    FootprintsIcon: IceSkatingOutlined,
    SmartphoneIcon: SmartphoneOutlined,
    CpuIcon: DevicesOtherOutlined,
    LaptopIcon: LaptopMacOutlined,
    TvIcon: TvOutlined,
    HomeIcon: KitchenOutlined,
    ArmchairIcon: ChairOutlined,
    ShoppingBasketIcon: LocalGroceryStoreOutlined,
    SparklesIcon: SpaOutlined,
    HeartPulseIcon: MonitorHeartOutlined,
    WatchIcon: WatchOutlined,
    GemIcon: DiamondOutlined,
    BriefcaseIcon: LuggageOutlined,
    DumbbellIcon: FitnessCenterOutlined,
    Gamepad2Icon: ToysOutlined,
    BabyIcon: ChildFriendlyOutlined,
    BookOpenIcon: MenuBookOutlined,
    CarIcon: DirectionsCarOutlined,
    WrenchIcon: HandymanOutlined,
    PaintbrushIcon: FormatPaintOutlined,
    DogIcon: PetsOutlined,
    Building2Icon: BusinessCenterOutlined,
    LightbulbIcon: LightbulbOutlined,
    RefrigeratorIcon: MicrowaveOutlined,
    GiftIcon: CardGiftcardOutlined,
    CompassIcon: ExploreOutlined,
    MusicIcon: PianoOutlined,
    FactoryIcon: FactoryOutlined,
    StoreIcon: StorefrontOutlined,
    HelpCircleIcon: CategoryOutlined,
    PackageIcon: CategoryOutlined,
    TagIcon: CategoryOutlined
};

/* Keyed on slug, for categories an admin created by hand without an icon. */
const BY_SLUG = {
    'fashion-clothing': CheckroomOutlined,
    footwear: IceSkatingOutlined,
    'mobile-tablets': SmartphoneOutlined,
    electronics: DevicesOtherOutlined,
    'computers-laptops': LaptopMacOutlined,
    'tv-home-entertainment': TvOutlined,
    'home-kitchen': KitchenOutlined,
    furniture: ChairOutlined,
    'beauty-personal-care': SpaOutlined,
    'health-wellness': MonitorHeartOutlined,
    watches: WatchOutlined,
    'jewellery-accessories': DiamondOutlined,
    'bags-luggage': LuggageOutlined,
    'sports-fitness': FitnessCenterOutlined,
    'toys-games': ToysOutlined,
    'baby-kids': ChildFriendlyOutlined,
    'books-stationery': MenuBookOutlined,
    automotive: DirectionsCarOutlined,
    'tools-hardware': HandymanOutlined,
    'home-improvement': FormatPaintOutlined,
    'pet-supplies': PetsOutlined,
    'office-supplies': BusinessCenterOutlined,
    'electrical-lighting': LightbulbOutlined,
    appliances: MicrowaveOutlined,
    'gifts-lifestyle': CardGiftcardOutlined,
    'travel-outdoor': ExploreOutlined,
    'musical-instruments': PianoOutlined,
    'industrial-business-supplies': FactoryOutlined,
    'local-specialty-products': StorefrontOutlined,
    other: CategoryOutlined
};

/**
 * Resolve a category to its icon element.
 *
 * @param category  the category object from /api/marketplace/categories, or a
 *                  bare slug string
 * @param size      rendered pixel size
 */
export const getCategoryIcon = (category, size = 22) => {
    const iconName = typeof category === 'string' ? '' : category?.icon;
    const slug = typeof category === 'string' ? category : category?.id;

    const Icon =
        BY_ICON_NAME[iconName] ||
        BY_SLUG[String(slug || '').toLowerCase()] ||
        CategoryOutlined;

    return <Icon style={{ fontSize: size }} aria-hidden="true" />;
};

export default getCategoryIcon;
